import React from 'react';
import { Box, Grid, Link, Stack, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useTranslation } from 'react-i18next';

import ProductLogo from '../components/ProductLogo';
import PaymentDetails from '../components/PaymentDetails';

const CreditsSection = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(2, 2, 4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 1.5, 3),
  },
}));

const CreditsCard = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 900,
  borderRadius: 0,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.common.white,
  boxShadow: theme.shadows[1],
  textAlign: 'center',
}));

const CreditLink = styled(Link)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  color: theme.palette.primary.main,
  textDecoration: 'none',
  fontWeight: 600,
}));

const CopyrightText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  color: theme.palette.text.secondary,
  fontSize: 12,
  lineHeight: 1.6,
}));

const CarBanner = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 1100,
  margin: '0 auto',
  marginTop: theme.spacing(4),
  padding: theme.spacing(2.5, 3),
  borderRadius: theme.shape.borderRadius,
  background: 'linear-gradient(120deg, #0f1c3a, #1f2f4d)',
  color: theme.palette.common.white,
  boxShadow: theme.shadows[4],
  [theme.breakpoints.down('sm')]: {
    width: 'calc(100% - 24px)',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
  },
}));

const CarTitle = styled(Typography)({
  fontWeight: 700,
  letterSpacing: 0.3,
  color: '#f6f9ff',
  textShadow: '0 1px 8px rgba(0, 0, 0, 0.35)',
});

const GuideBanner = styled(Link)(({ theme }) => ({
  width: '100%',
  maxWidth: 1100,
  margin: `${theme.spacing(2)} auto 0`,
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.shape.borderRadius,
  background: '#102943',
  color: theme.palette.common.white,
  boxShadow: theme.shadows[3],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.25),
  textDecoration: 'none',
  fontWeight: 700,
  transition: theme.transitions.create(['background-color', 'transform']),
  '&:hover': {
    background: '#16395e',
    transform: 'translateY(-1px)',
  },
  [theme.breakpoints.down('sm')]: {
    width: 'calc(100% - 24px)',
    padding: theme.spacing(1.25, 1.5),
    alignItems: 'center',
    textAlign: 'center',
  },
}));

function Footer() {
  const { t } = useTranslation();

  return (
    <Box component="footer" sx={{ bgcolor: 'secondary.light' }}>
      <Grid
        container
        spacing={3}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
          flexwWrap: 'nowrap',
          justifyContent: 'space-evenly',
        }}
      >
        <Grid
          item
          lg={6}
          sx={{
            my: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <PaymentDetails />
        </Grid>
        <Grid
          item
          lg={6}
          sx={{
            my: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <ProductLogo />
          <Stack spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
            <CopyrightText>2026 All rights reserved.</CopyrightText>
            <CopyrightText>
              Powered by{' '}
              <Link
                href="https://nataliaki.eu/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'secondary.main',
                  textDecoration: 'none',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                © NataliaKi
              </Link>
            </CopyrightText>
          </Stack>
        </Grid>
      </Grid>
      <CarBanner>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'center', sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src="/carsnk/logo.png"
              alt="CarsNK"
              width={72}
              height={72}
              loading="lazy"
              decoding="async"
              sx={{
                width: 72,
                height: 72,
                borderRadius: 1.5,
                objectFit: 'cover',
                flexShrink: 0,
              }}
            />
            <Box>
              <CarTitle
                variant="h6"
                sx={{ textAlign: { xs: 'center', sm: 'left' } }}
              >
                Drive Chalkidiki with ease — rent a car in Nea Kallikratia
              </CarTitle>
              <Typography
                variant="body1"
                sx={{
                  mt: 0.75,
                  color: 'rgba(255,255,255,0.96)',
                  textShadow: '0 1px 6px rgba(0, 0, 0, 0.35)',
                  fontSize: 15,
                  lineHeight: 1.5,
                  fontWeight: 600,
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                CarsNK.gr: quick pickup, airport delivery on request, friendly
                local support.
              </Typography>
            </Box>
          </Stack>
          <Button
            component={Link}
            href="https://carsnk.gr/"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            color="secondary"
            sx={{
              color: 'common.white',
              width: { xs: '100%', sm: 'auto' },
              px: 2.5,
              py: 1,
              minWidth: 180,
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: 3,
            }}
          >
            Book at CarsNK.gr
          </Button>
        </Stack>
      </CarBanner>
      <GuideBanner
        href="https://kalikratia.bbqr.site/en"
        target="_blank"
        rel="noopener noreferrer sponsored"
      >
        <Box
          component="img"
          src="/nea-kalikratia/white.png"
          alt=""
          aria-hidden="true"
          sx={{ width: 34, height: 34, objectFit: 'contain' }}
        />
        <span>{t('footer.kallikratiaGuide')}</span>
      </GuideBanner>
      <CreditsSection>
        <CreditsCard>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1.5, sm: 2 }}
            alignItems="center"
            justifyContent="center"
          >
            <CreditLink
              href="https://www.bbqr.site"
              target="_blank"
              rel="noopener noreferrer"
            >
              <QrCode2Icon sx={{ fontSize: 20 }} />
              BBQR - Solutions for Travel & Hospitality
            </CreditLink>
          </Stack>
        </CreditsCard>
      </CreditsSection>
    </Box>
  );
}

export default Footer;

// function PModal({ open, onClose, closeModal }) {
//   if (!open) return null;

//   const modalStyles = {
//     display: 'flex',
//     flexDirection: 'column',
//     alignItems: 'center',
//     justifyContent: 'center',
//     position: 'fixed',
//     top: 0,
//     left: 0,
//     width: '100%',
//     height: '100%',
//     backgroundColor: 'rgba(0, 0, 0, 0.5)',
//     zIndex: 9999,
//   };

//   const contentStyles = {
//     backgroundColor: '#fff',
//     padding: '0px',
//     maxWidth: '100%',
//     maxHeight: '100%',
//     overflowY: 'auto',
//     alignItems: 'center',
//     textAlign: 'end',
//   };

//   return (
//     <Box style={modalStyles}>
//       <Box style={contentStyles}>
//         <Presentation closeModal={closeModal} />
//         <Button onClick={onClose} color="primary">
//           Close
//         </Button>
//       </Box>
//     </Box>
//   );
// }
